import { Info } from '@mui/icons-material';
import { IconButton, ImageListItem, ImageListItemBar } from '@mui/material';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ETextureOrigin, IProductTexture, ProductImageListItemProps } from '../../@types/ContextProviderRelated';
import { EActiveDrawer } from '../../@types/Enums';
import { EAssetType, IFilesApiColor, TTexture } from '../../@types/FilesAPIModels';
import { EPendingType } from '../../@types/PendingItemTypes';
import { useProductActions } from '../../provider/BuildingProvider/actions/useProductActions';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { usePendingItem } from '../../provider/PendingItemProvider';
import { useSelection } from '../../provider/SelectionProvider';
import { getProductImageListItemBarStyles, getProductImageListItemIconButtonStyles, getProductImageListItemStyles } from '../../utils/imgUtils';
import { createProductModelFromApi } from '../../utils/modelFactory'; // Import the factory
import { convertColorAssetToTTexture, convertMaterialToTTexture, loadProductMaterialTexture } from '../../utils/textureLoaderUtility';
import AssetSelectionModal from '../modals/AssetSelectionModal';
import LazyImage from './LazyImage';
import RoomComponentTooltipContainer from './RoomComponentTooltipContainer';
import Tooltip from './Tooltip';

const ProductImageListItem = ({ product, index }: ProductImageListItemProps): JSX.Element => {
  const { setBuilding } = useBuilding();
  const { addProduct } = useProductActions(setBuilding); // Use specific hook
  const { startPlacement } = usePendingItem(); 
  
  const [modalOpen, setModalOpen] = useState(false);
  const [availableAssets, setAvailableAssets] = useState<TTexture[]>([]);
  const [isLoading, setIsLoading] = useState(false); // New loading state
  const { setActiveDrawer } = useSelection();
  const intl = useIntl();

  /**
   * Main logic: 
   * 1. Create full model (fetch SVG/Dims)
   * 2. Start Ghost
   * 3. Define Confirm Action
   */
  const handleStartGhost = async (textureInfo?: IProductTexture) => {
    try {
      setIsLoading(true);
      
      // 1. Create the full internal model
      const fullModel = await createProductModelFromApi(product, textureInfo);
      
      setIsLoading(false);

      if (!fullModel) {
        console.error("Failed to create product model");
        return;
      }

      // 2. Start Ghost Placement
      startPlacement({
        type: EPendingType.PRODUCT,
        data: { productModel: fullModel }, // Pass the REAL model
        
        // 3. Define Add Action
        onConfirm: (position, spaceUuid) => {
           // Update position one last time (cloned in handlePointerDown)
           fullModel.position = position; 
           
           // Add directly (no fetching needed inside addProduct)
           addProduct(fullModel, spaceUuid);
        }
      });
      setActiveDrawer(EActiveDrawer.NONE)
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  };

  const handleAssetSelection = async (asset: TTexture | null) => {
    let textureInfo: IProductTexture | undefined;

    if (asset) {
      if (asset.assetType === EAssetType.MATERIAL) {
        try {
          const threeTexture = await loadProductMaterialTexture(product.id, asset.id);
          textureInfo = {
            textureOrigin: ETextureOrigin.PRODUCT,
            id: product.id,
            assetId: asset.id,
            name: asset.name,
            type: EAssetType.MATERIAL,
            material: threeTexture,
            color: undefined,
            rotation: 0,
            offset: 0,
          };
        } catch (err) {
          console.error('Error loading product material texture:', err);
          return;
        }
      } else if (asset.assetType === EAssetType.COLOR) {
        textureInfo = {
          textureOrigin: ETextureOrigin.PRODUCT,
          id: product.id,
          assetId: asset.id,
          name: asset.name,
          type: EAssetType.COLOR,
          material: null,
          color: (asset as IFilesApiColor).hex,
          rotation: 0,
          offset: 0,
        };
      }
    }

    setModalOpen(false);
    // Proceed to create ghost with texture
    await handleStartGhost(textureInfo);
  };

  const handleClick = async () => {
    if (isLoading) return;

    const materialAssets = product.assets?.filter((a) => a.assetType === EAssetType.MATERIAL) || [];
    const colorAssets = product.assets?.filter((a) => a.assetType === EAssetType.COLOR) || [];
    const assets = [...materialAssets.map(convertMaterialToTTexture), ...colorAssets.map(convertColorAssetToTTexture)];

    if (assets.length === 1) {
      await handleAssetSelection(assets[0]);
    } else if (assets.length > 1) {
      setAvailableAssets(assets);
      setModalOpen(true);
    } else {
      // No assets, proceed immediately
      await handleStartGhost(undefined);
    }
  };

  return (
    <>
      <ImageListItem 
        key={`${product.name}-${index}`} 
        cols={1} 
        rows={1} 
        sx={{
            ...getProductImageListItemStyles(),
            opacity: isLoading ? 0.5 : 1,
            cursor: isLoading ? 'wait' : 'pointer'
        }} 
        onClick={handleClick}
      >
        <LazyImage item={product} />
        <ImageListItemBar
          key={`${product.name}-imgBar`}
          className="imgBar"
          title={product.name}
          subtitle={product.manufacturer?.name || ''}
          actionIcon={
            <Tooltip
              PopperProps={{
                modifiers: [
                  { name: 'preventOverflow', options: { boundary: 'window' } },
                  { name: 'offset', options: { offset: [8, 0] } },
                ],
              }}
              title={<RoomComponentTooltipContainer roomComponent={product} />}
              placement="top-start"
            >
              <IconButton className="iconBtn" sx={getProductImageListItemIconButtonStyles()} aria-label={`info about ${product.name}`}>
                <Info />
              </IconButton>
            </Tooltip>
          }
          sx={getProductImageListItemBarStyles()}
        />
      </ImageListItem>

      <AssetSelectionModal
        open={modalOpen}
        title={intl.formatMessage({ id: 'ProductSelection.selectSurfaceTitle' }, { productName: product.name })}
        assets={availableAssets}
        onClose={() => setModalOpen(false)}
        onSelect={handleAssetSelection}
        origin={ETextureOrigin.PRODUCT}
        productId={product.id}
      />
    </>
  );
};

export default ProductImageListItem;